import { useCallback, useMemo, memo } from 'react';
import { lambdaIamPoliciesRelativeToGroup } from 'config-editor-common';
import type { Lambda, LambdaDefaults } from 'config-editor-common';
import { LambdaItemEditor } from './LambdaComponents/phx-api-v2/LambdaItemEditor';
import type { LambdaNameInputRef } from './LambdaComponents/shared/LambdaNameInput';

export interface LambdaListEditorRowProps {
  name: string;
  /** Raw lambda from config (stable reference when this row did not change). */
  lambda: Lambda;
  defaults?: LambdaDefaults;
  iamPolicyNames: string[];
  customIamPolicyNames: string[];
  buildNumber: boolean;
  onUpdate: (
    name: string,
    field: keyof Lambda,
    value: string | number | boolean | Record<string, string> | string[],
  ) => void;
  onRename: (oldName: string, newName: string) => void;
  onDelete: (name: string) => void;
  registerContainer: (name: string, el: HTMLDivElement | null) => void;
  registerNameInput: (name: string, ref: LambdaNameInputRef | null) => void;
}

function LambdaListEditorRowInner({
  name,
  lambda,
  defaults,
  iamPolicyNames,
  customIamPolicyNames,
  buildNumber,
  onUpdate,
  onRename,
  onDelete,
  registerContainer,
  registerNameInput,
}: LambdaListEditorRowProps) {
  const effectiveLambda = useMemo(
    () => ({
      ...defaults,
      ...lambda,
      iam_policy_names: lambdaIamPoliciesRelativeToGroup(lambda.iam_policy_names, iamPolicyNames),
      iam_policy_names_custom: lambdaIamPoliciesRelativeToGroup(
        lambda.iam_policy_names_custom,
        customIamPolicyNames,
      ),
    }),
    [defaults, lambda, iamPolicyNames, customIamPolicyNames],
  );

  const setContainerRef = useCallback(
    (el: HTMLDivElement | null) => {
      registerContainer(name, el);
    },
    [registerContainer, name],
  );

  const setNameInputRef = useCallback(
    (ref: LambdaNameInputRef | null) => {
      registerNameInput(name, ref);
    },
    [registerNameInput, name],
  );

  return (
    <div ref={setContainerRef}>
      <LambdaItemEditor
        ref={setNameInputRef}
        name={name}
        lambda={effectiveLambda}
        buildNumber={buildNumber}
        onUpdate={onUpdate}
        onRename={onRename}
        onDelete={onDelete}
      />
    </div>
  );
}

function rowPropsEqual(prev: LambdaListEditorRowProps, next: LambdaListEditorRowProps): boolean {
  return (
    prev.name === next.name &&
    prev.lambda === next.lambda &&
    prev.defaults === next.defaults &&
    prev.iamPolicyNames === next.iamPolicyNames &&
    prev.customIamPolicyNames === next.customIamPolicyNames &&
    prev.buildNumber === next.buildNumber &&
    prev.onUpdate === next.onUpdate &&
    prev.onRename === next.onRename &&
    prev.onDelete === next.onDelete &&
    prev.registerContainer === next.registerContainer &&
    prev.registerNameInput === next.registerNameInput
  );
}

export const LambdaListEditorRow = memo(LambdaListEditorRowInner, rowPropsEqual);
